/**
 * ChatInput — send-a-message box with @mention suggestions and the artist
 * track-drop shortcut. Text send goes through REST so AI moderation runs
 * server-side; the WS just receives the broadcast.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Send, Music, Loader2 } from "lucide-react";
import { toast } from "sonner";
import TrackDropPicker from "@/components/chat/TrackDropPicker";

export default function ChatInput({ room }) {
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [mentions, setMentions] = useState([]);
  const [mentionQ, setMentionQ] = useState(null);
  const inputRef = useRef(null);
  const canDrop = user && (user.is_artist || user.role === "admin");

  const send = async () => {
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true);
    try {
      await api.post(`/chat/${room}/messages`, { text: t, kind: "text" });
      setText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Message failed");
    } finally { setBusy(false); }
  };

  const sendTrackDrop = async (track) => {
    setBusy(true);
    try {
      await api.post(`/chat/${room}/messages`, {
        text: text.trim() || `Dropped a track: ${track.title}`,
        kind: "track_drop",
        track_id: track.id,
      });
      setText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't drop track");
    } finally { setBusy(false); }
  };

  // Lightweight @mention suggestion: debounce-query users by prefix.
  useEffect(() => {
    if (mentionQ === null) { setMentions([]); return; }
    const h = setTimeout(async () => {
      try {
        const { data } = await api.get(`/artists?limit=8`);
        const q = mentionQ.toLowerCase();
        const list = (data.artists || []).filter((a) =>
          (a.username || "").toLowerCase().startsWith(q) ||
          (a.display_name || "").toLowerCase().startsWith(q),
        ).slice(0, 6);
        setMentions(list);
      } catch { /* silent */ }
    }, 200);
    return () => clearTimeout(h);
  }, [mentionQ]);

  const onChange = (e) => {
    const v = e.target.value;
    setText(v);
    // Detect a trailing @word for suggestions.
    const m = /@([a-zA-Z0-9_]{1,32})$/.exec(v);
    setMentionQ(m ? m[1] : null);
  };

  const insertMention = (u) => {
    const v = text.replace(/@([a-zA-Z0-9_]{1,32})$/, `@${u.username} `);
    setText(v);
    setMentionQ(null);
    inputRef.current && inputRef.current.focus();
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="relative border-t border-[#2A2A2A] bg-[#0A0A0A] p-3">
      {mentions.length > 0 && (
        <div className="absolute bottom-full left-3 right-3 mb-1 border border-[#2A2A2A] bg-[#0A0A0A] max-h-48 overflow-y-auto">
          {mentions.map((u) => (
            <button
              key={u.id}
              data-testid={`chat-mention-suggestion-${u.username}`}
              onClick={() => insertMention(u)}
              className="w-full text-left px-3 py-1.5 hover:bg-white/5 flex items-center gap-2"
            >
              <span className="font-display text-sm">{u.display_name || u.username}</span>
              <span className="font-mono text-[10px] text-gray-500">@{u.username}</span>
            </button>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        {canDrop && (
          <button
            data-testid="chat-open-track-drop"
            onClick={() => setPickerOpen(true)}
            disabled={busy}
            title="Drop one of your tracks"
            className="h-10 w-10 grid place-items-center border border-[#2A2A2A] bg-black hover:border-[#FF4500] hover:text-[#FF4500]"
          >
            <Music className="h-4 w-4" />
          </button>
        )}
        <input
          ref={inputRef}
          data-testid="chat-input"
          value={text}
          onChange={onChange}
          onKeyDown={onKeyDown}
          placeholder={`Message #${room}`}
          disabled={busy}
          className="flex-1 bg-black border border-[#2A2A2A] px-3 h-10 font-mono text-sm focus:outline-none focus:border-[#FF4500] disabled:opacity-50"
          maxLength={1000}
        />
        <button
          data-testid="chat-send"
          onClick={send}
          disabled={busy || !text.trim()}
          className="h-10 px-4 bg-[#FF4500] hover:bg-[#E53E00] text-black font-mono text-xs uppercase tracking-widest disabled:opacity-40 flex items-center gap-2"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Send
        </button>
      </div>
      <TrackDropPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onPick={sendTrackDrop} />
    </div>
  );
}
